import { Component, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Constants, pageInContext, transactionDetailLabels } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { selectAchInfo, selectCheckInfo, selectInternalTransferInfo, selectMoneyLinkType, selectStagedTransactionForm } from '../../../../../create-request/reducers';
import { combineLatest, filter, Subject, takeUntil } from 'rxjs';
import { AchExtraPrompts, isAchForm, isCheckTransactionForm, isInternalTransferForm, isWireForm } from '../../../../../create-request/models/form/TransactionForm';
import { selectIsStandingAuthViewPage, selectIsTransactionsViewPage, selectViewFrequency } from 'projects/money-movement-ui/src/app/transaction-view/reducers';
import { selectAchInstructions, selectInfo } from 'projects/money-movement-ui/src/app/account-info-header/reducers';
import { selectViewInContext } from 'projects/money-movement-ui/src/app/shared/reducers';
import { selectHasAccessInitiateMoneyMovement } from 'projects/money-movement-ui/src/app/authorize/reducers';
 import { CheckExtraPrompts } from 'projects/money-movement-ui/src/app/standing-authorizations/models/standingAuthorizationForm';
import { FormatPercentPipe } from '../../../../pipes/FormatPercentPipe';

@Component({
  standalone: false,
  selector: 'mm-ptrs-details-condensed',
  templateUrl: './ptrs-details-condensed.component.html',
  styleUrl: './ptrs-details-condensed.component.scss'
})
export class PtrsDetailsCondensedComponent implements OnInit {
  destroy$: Subject<boolean> = new Subject<boolean>();
  isTransactionViewPage: boolean = false;
  isStandingAuthViewPage: boolean = false;
  transferDirection: string = '';
  readonly labels = transactionDetailLabels;
  trustIncomePercent: string | undefined;
  trustPrincipalPercent: string | undefined;
  trustTaxCategory: string = '';
  trustTaxCode: string | undefined;
  trustPaidFor: string | undefined;
  trustComment: string = '';
  viewPagePtrsInfoBannerPrimaryText = Constants.viewPagePtrsInfoBannerPrimaryText;
  viewPagePtrsInfoBannerBoldText = Constants.viewPagePtrsInfoBannerBoldText;
  frequencyOfOccurrence: string;
  moneyLinkType: string;
  isRecurringSetup: boolean;
  transactionType: string = '';
  trustIncomeToPercent: string = '';
  trustPrincipalToPercent: string = '';
  depositTaxCode: string;
  fromAccountTitle: string = 'From account settings';
  toAccountTitle: string = 'To account settings';
  isToAccountTrust: boolean;
  isPtrs: boolean;
  isViewPage: boolean = false;

  constructor(private store: Store, private percentFormatPipe: FormatPercentPipe) {
  }

  ngOnInit(): void {
    combineLatest([
      this.store.pipe(select(selectStagedTransactionForm)),
      this.store.pipe(select(selectAchInfo)),
      this.store.select(selectCheckInfo),
      this.store.select(selectInternalTransferInfo)
    ]).pipe(takeUntil(this.destroy$)
    ).subscribe(([form, achInfo, checkInfo, itInfo]) => {
      this.transactionType = form?.transactionType;
      if (form?.transactionType && isAchForm(form)) {
        const transactionDetails = form.transactionDetails;
        const extraPrompts = form.extraPrompts as AchExtraPrompts;
        this.trustIncomePercent = transactionDetails?.trustIncomePercent;
        this.trustPrincipalPercent = transactionDetails?.trustPrincipalPercent;
        this.trustTaxCategory = transactionDetails?.trustTaxCategory ?? '';
        this.trustTaxCode = transactionDetails?.trustTaxCode;
        this.trustPaidFor = transactionDetails?.trustPaidFor;
        this.trustComment = transactionDetails?.trustComment ?? '';
        this.transferDirection = extraPrompts?.transferDirection;
        this.isRecurringSetup = extraPrompts?.frequency === 'recurring';
        this.frequencyOfOccurrence = this.isRecurringSetup ? achInfo.frequency ? achInfo.frequency : achInfo.frequencyList.find(frequency => frequency.code === transactionDetails.recurringFrequency)!.description : '';
      }
      else if (isCheckTransactionForm(form)) {
        let extraPrompts = form.extraPrompts as CheckExtraPrompts;
        let transactionDetails = form.transactionDetails;
        this.trustIncomePercent = transactionDetails!.trustIncomePercent ?? '';
        this.trustPrincipalPercent = transactionDetails!.trustPrincipalPercent ?? '';
        this.trustTaxCode = transactionDetails!.trustTaxCode ?? '';
        this.trustPaidFor = transactionDetails!.trustPaidFor ?? '';
        this.trustTaxCategory = transactionDetails.trustTaxCategory ?? '';
        this.trustComment = transactionDetails.trustComment ?? '';
      }
      else if (isInternalTransferForm(form)) {
        let transactionDetails = form.transactionDetails;
        this.trustIncomePercent = this.percentFormatPipe.transform(transactionDetails!.trustIncomePercent!);
        this.trustPrincipalPercent = this.percentFormatPipe.transform(transactionDetails!.trustPrincipalPercent!);
        this.trustTaxCode = transactionDetails!.trustTaxCode!;
        this.trustPaidFor = transactionDetails!.trustPaidFor!;
        this.trustComment = transactionDetails?.trustComment ?? '';
        this.trustTaxCategory = transactionDetails?.trustTaxCategory ?? '';
        this.trustIncomeToPercent = transactionDetails?.trustIncomeToPercent ?? '';
        this.trustPrincipalToPercent = transactionDetails?.trustPrincipalToPercent ?? '';
        this.depositTaxCode = transactionDetails?.trustDepositCode ?? '';
        this.isToAccountTrust = itInfo.toAccountInfo.isTrust;
      } else if (isWireForm(form)) {
        let transactionDetails = form.transactionDetails;
        this.trustIncomePercent = this.percentFormatPipe.transform(transactionDetails!.trustIncomePercent!);
        this.trustPrincipalPercent = this.percentFormatPipe.transform(transactionDetails!.trustPrincipalPercent!);
        this.trustTaxCode = transactionDetails!.trustTaxCode!;
        this.trustPaidFor = transactionDetails!.trustPaidFor!;
        this.trustComment = transactionDetails?.trustComment ?? '';
        this.trustTaxCategory = transactionDetails?.trustTaxCategory ?? '';
      }
    });

    this.store.select(selectInfo).pipe(takeUntil(this.destroy$)).subscribe(info => { this.isPtrs = info.classifications.isPtrs });
    this.store.select(selectIsTransactionsViewPage).pipe(takeUntil(this.destroy$)).subscribe(isTransactionViewPage => this.isTransactionViewPage = isTransactionViewPage);
    this.store.select(selectIsStandingAuthViewPage).pipe(takeUntil(this.destroy$)).subscribe(isStandingAuthViewPage => this.isStandingAuthViewPage = isStandingAuthViewPage);
    this.store.select(selectViewInContext).pipe(takeUntil(this.destroy$)).subscribe(viewInContext => this.isViewPage = viewInContext === pageInContext.view || viewInContext === pageInContext.viewMsv);

    combineLatest([
      this.store.select(selectAchInstructions),
      this.store.select(selectViewInContext),
      this.store.select(selectViewFrequency),
      this.store.select(selectHasAccessInitiateMoneyMovement),
    ]).pipe(takeUntil(this.destroy$)
    ).subscribe(([instructions, step, frequency, hasInitReleaseAccess]) => {
      if (hasInitReleaseAccess && !instructions?.length) {
        if (step === pageInContext.view) {
          this.frequencyOfOccurrence = frequency ?? '';
        }
      }
    });

    this.store.select(selectMoneyLinkType).pipe(takeUntil(this.destroy$), filter(_ => this.isStandingAuthViewPage || this.isTransactionViewPage)).subscribe(moneyLinkType => {
      this.moneyLinkType = moneyLinkType;
    });
  }

  isNotEmptyOrNull(param: any) {
    const params = param?.trim();
    if (params === null || params === undefined || params === 'undefined%' || params === '') {
      return false;
    }
    return true;
  }

  get isRecurring() {
    // frequency is not set on the transaction flow so this.frequency is needed to not show the end date on the review page
    return this.frequencyOfOccurrence && this.frequencyOfOccurrence !== 'On-Request';
  }

  get displayPtrsBanner() {
    if (this.transactionType === 'internal-transfer' || this.transactionType === 'check') {
      return this.isPtrs && (this.isStandingAuthViewPage || this.isTransactionViewPage)
    }
    else if (this.transactionType === 'ach') {
      // moneyLinkType will say 'On-Request' when creating or using a standing authorization
      // this will return true only if it's a regular ACH transaction (not a pending SLOA or recurring transaction)
      // that a user is viewing from transactions table
      return this.isPtrs && this.isTransactionViewPage && !this.moneyLinkType.includes('On-Request') && !this.isRecurring;
    }
  }

  get displayToTrustFields() {
    return this.isToAccountTrust && (this.depositTaxCode || this.isStandingAuthViewPage);
  }
}
